import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, InputNumber, Select, Switch } from "antd";
import { useFeeStructures } from "../../../hooks/finance/useFeeStructures";
import { useFeeTypes } from "../../../hooks/finance/useFeeTypes";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const DAY_ONLY_CLASS_NAMES = ["Baby Class", "Middle Class", "Top Class"];

const classSupportsBoardingType = (cls, boardingType) => {
  const isDayOnly = DAY_ONLY_CLASS_NAMES.includes(cls.name);

  if (boardingType === "Day") return true;
  if (boardingType === "Boarding") return !isDayOnly;
  if (boardingType === "Both") return !isDayOnly;

  return true;
};

const FeeStructureModal = ({ visible, onCancel, onSuccess, feeStructure }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { createFeeStructure, updateFeeStructure } = useFeeStructures();
  const { feeTypes, fetchFeeTypes } = useFeeTypes();
  const {
    academicYears,
    currentAcademicYear,
    fetchAcademicYears,
    fetchCurrentAcademicYear,
  } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  const isEditing = !!feeStructure;
  const selectedBoardingType = Form.useWatch("boarding_type", form) || "Both";

  const filteredClasses = useMemo(
    () =>
      classes.filter((cls) =>
        classSupportsBoardingType(cls, selectedBoardingType),
      ),
    [classes, selectedBoardingType],
  );

  useEffect(() => {
    if (visible) {
      // Only fetch active fee types
      fetchFeeTypes({ filters: { is_active: 1 } });
      fetchClasses();
      fetchTerms();
      fetchAcademicYears();
      fetchCurrentAcademicYear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible && feeStructure) {
      form.setFieldsValue({
        name: feeStructure.name,
        fee_type_id: feeStructure.feeTypeId,
        school_class_id: feeStructure.schoolClassId,
        term_id: feeStructure.termId,
        acadyr: feeStructure.acadyr,
        boarding_type: feeStructure.boardingType || "Both",
        amount: parseFloat(feeStructure.amount),
        is_mandatory: feeStructure.isMandatory === true,
        description: feeStructure.description,
      });
    } else if (visible) {
      form.resetFields();
      // Set default academic year from hook
      if (currentAcademicYear?.acadyr) {
        form.setFieldsValue({
          acadyr: currentAcademicYear.acadyr,
          boarding_type: "Both",
        });
      }
    }
  }, [visible, feeStructure, form, currentAcademicYear]);

  useEffect(() => {
    if (!visible) return;

    const selectedClassId = form.getFieldValue("school_class_id");
    if (!selectedClassId) return;

    const selectedClass = classes.find((cls) => cls.id === selectedClassId);
    if (
      selectedClass &&
      !classSupportsBoardingType(selectedClass, selectedBoardingType)
    ) {
      form.setFieldsValue({ school_class_id: null });
    }
  }, [visible, classes, form, selectedBoardingType]);

  const fetchClasses = async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.backend}/bootstrap/klass`,
        null,
        navigate,
      );
      if (response.ok) {
        setClasses(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const selectedClass = classes.find(
        (cls) => cls.id === values.school_class_id,
      );

      if (
        selectedClass &&
        DAY_ONLY_CLASS_NAMES.includes(selectedClass.name) &&
        ["Boarding", "Both"].includes(values.boarding_type)
      ) {
        form.setFields([
          {
            name: "boarding_type",
            errors: ["This class only supports Day students."],
          },
        ]);
        return;
      }

      const data = {
        name: values.name,
        feeTypeId: values.fee_type_id,
        schoolClassId: values.school_class_id || null,
        termId: values.term_id || null,
        acadyr: values.acadyr,
        boardingType: values.boarding_type || "Both",
        amount: values.amount,
        isMandatory: values.is_mandatory ? true : false,
        description: values.description || null,
      };

      setLoading(true);
      let result;
      if (isEditing) {
        result = await updateFeeStructure(feeStructure.feeStructureId, data);
      } else {
        result = await createFeeStructure(data);
      }

      if (result) {
        onSuccess();
      }
    } catch (error) {
      console.error("Validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEditing ? "Edit Fee Structure" : "Create Fee Structure"}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={isEditing ? "Update" : "Create"}
      confirmLoading={loading}
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          is_mandatory: true,
          boarding_type: "Both",
        }}
      >
        <Form.Item
          label="Fee Structure Name"
          name="name"
          rules={[
            { required: true, message: "Please enter fee structure name" },
            { max: 200, message: "Name must be less than 200 characters" },
          ]}
          tooltip="Descriptive name for this fee structure"
        >
          <Input placeholder="e.g., Primary 5 Tuition - Term 1 2025" />
        </Form.Item>

        <Form.Item
          label="Fee Type"
          name="fee_type_id"
          rules={[{ required: true, message: "Please select fee type" }]}
        >
          <Select placeholder="Select fee type" loading={feeTypes.length === 0}>
            {feeTypes.map((type) => (
              <Option key={type.feeTypeId} value={type.feeTypeId}>
                {type.name} ({type.code})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Day/Boarding"
          name="boarding_type"
          rules={[
            { required: true, message: "Please select day/boarding target" },
          ]}
          tooltip="Choose whether this fee applies to all students, day students only, or boarding students only"
        >
          <Select placeholder="Select target students">
            <Option value="Both">Both</Option>
            <Option value="Day">Day Students</Option>
            <Option value="Boarding">Boarding Students</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Class"
          name="school_class_id"
          tooltip="Leave empty for fees applicable to all classes"
        >
          <Select placeholder="Select class (optional)" allowClear>
            {filteredClasses.map((cls) => (
              <Option key={cls.id} value={cls.id}>
                {cls.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Term"
          name="term_id"
          tooltip="Leave empty for annual fees"
        >
          <Select placeholder="Select term (optional)" allowClear>
            {Array.isArray(terms) &&
              terms.map((term) => (
                <Option key={term.id} value={term.id}>
                  {term.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Academic Year"
          name="acadyr"
          rules={[{ required: true, message: "Please select academic year" }]}
        >
          <Select
            placeholder="Select academic year"
            showSearch
            optionFilterProp="children"
          >
            {Array.isArray(academicYears) &&
              academicYears.map((year) => (
                <Option key={year.id} value={year.acadyr}>
                  {year.acadyr}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Amount (UGX)"
          name="amount"
          rules={[
            { required: true, message: "Please enter amount" },
            {
              type: "number",
              min: 0,
              message: "Amount must be greater than 0",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="e.g., 500000"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} placeholder="Optional description" />
        </Form.Item>

        <Form.Item
          label="Mandatory Fee"
          name="is_mandatory"
          valuePropName="checked"
          tooltip="Required fees must be paid by students"
          extra="Mandatory fees are marked as required in student bills."
        >
          <Switch checkedChildren="Required" unCheckedChildren="Optional" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FeeStructureModal;
